import getApiProxy from '../proxies/apiProxy';
import { BodyInput, EndpointSpec, ParamsInput, pathFor, QueryInput, ReplyOf } from '../contract/common';

// Calls an endpoint described in the API contract (src/contract): builds the address and request from it, and checks
// that what comes back is what the contract promises. A response that isn't throws a ContractError, which the app's
// global handler logs with the endpoint and the fields that were wrong, instead of the mismatch surfacing later as an
// `undefined` somewhere in the UI. Failures of the request itself (network, status codes) throw as axios errors.

export class ContractError extends Error {
    constructor(spec: EndpointSpec, public readonly problems: string[]) {
        super(`Unexpected response from ${spec.method} ${spec.path}: ${problems.join('; ')}`);
        this.name = 'ContractError';
    }
}

type Part<Key extends string, Value> = [Value] extends [never] ? {} : { [K in Key]: Value };

// What a call must supply: only the parts the endpoint has.
export type RequestOf<E extends EndpointSpec> =
    Part<'params', ParamsInput<E>> & Part<'query', QueryInput<E>> & Part<'body', BodyInput<E>>;

export async function callApi<E extends EndpointSpec>(
    spec: E,
    ...args: keyof RequestOf<E> extends never ? [] : [RequestOf<E>]
): Promise<ReplyOf<E>> {
    const { params, query, body } = (args[0] ?? {}) as { params?: Record<string, string>, query?: unknown, body?: unknown };

    const api = await getApiProxy();
    const response = await api.request({
        method: spec.method,
        url: `api${pathFor(spec.path, params)}`,
        params: query,
        data: body,
    });

    const result = spec.response.safeParse(response.data);
    if (!result.success) {
        throw new ContractError(spec, result.error.issues.slice(0, 5).map((issue) => `${issue.path.join('.') || 'response'}: ${issue.message}`));
    }

    return result.data;
}
